import { memo, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import {
  clearMessages,
  clearSelectedChatTopic,
  clearThinkingMessages,
  setSelectedChatTopic,
  setSelectedUser,
} from '@/store/chat/actions';

import { selectChat } from '@/store/chat/selectors';
import { selectUsers } from '@/store/users/selectors';

import { Avatar } from 'antd';

import useImage from '@shared/hooks/useImage';

import classNames from 'classnames';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';

import styles from '../ChatsList.module.scss';

const ChatElement = ({ data, isTopic = false }) => {
  const avatar = useImage(data?.chatPhotoUuid, 'groups');
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();

  const { t } = useTranslation('chat');
  const { currentUser } = useSelector(selectUsers);
  const { selectedUser, selectedChatTopic } = useSelector(selectChat);

  useEffect(() => {
    const initialValues = {};

    for (const [key, value] of searchParams.entries()) {
      if (value !== undefined && value?.length) {
        if (value.includes(',')) {
          initialValues[key] = value.split(',');
        } else {
          initialValues[key] = value;
        }
      }
    }

    if (Number(initialValues.chatId) === data.chatId) {
      dispatch(
        setSelectedUser({
          photo: data.chatPhotoUuid,
          ...data,
        })
      );
      setSearchParams({});
    }
  }, [searchParams]);

  const isMessageToday = useMemo(() => {
    const date = new Date();
    const format = 'DD:MM:YYYY';
    if (data?.lastMessage) {
      return dayjs(date).format(format) === dayjs(data?.lastMessage?.createdAt).format(format);
    } else {
      return false;
    }
  }, [data?.lastMessage]);

  const isMessageOnTheWeek = useMemo(() => {
    const date = new Date();
    if (data?.lastMessage) {
      return (
        dayjs(date)?.week() === dayjs(data?.lastMessage?.createdAt)?.week() &&
        dayjs(date)?.year() === dayjs(data?.lastMessage?.createdAt)?.year()
      );
    } else {
      return false;
    }
  }, [data?.lastMessage]);

  const handleSelectGroup = () => {
    const saveData = {
      photo: data.chatPhotoUuid,
      ...data,
    };

    if (isTopic) {
      // Очищаем предыдущую историю сообщений при переключении на другую тему
      if (selectedChatTopic?.chatId !== data?.chatId) {
        dispatch(clearMessages());
      }
      dispatch(setSelectedChatTopic(saveData));
    } else if (selectedUser?.chatId !== data?.chatId) {
      // Очищаем предыдущую историю сообщений при переключении на другой чат
      dispatch(clearMessages());
      dispatch(setSelectedUser(saveData));
      dispatch(clearSelectedChatTopic());
    }
  };

  const renderDate = () => {
    if (isMessageToday) return dayjs(data?.lastMessage?.createdAt).format('HH:mm');
    else if (isMessageOnTheWeek) return dayjs(data?.lastMessage?.createdAt).format('dd');
    else return dayjs(data?.lastMessage?.createdAt).format('DD.MM.YYYY');
  };

  const renderFileText = () => {
    if (data.lastMessage?.files?.length >= 2) {
      return t('files.0');
    } else if (data.lastMessage?.files?.length === 1) {
      if (data.lastMessage.files[0].fileType.includes('image')) {
        return t('files.2');
      } else {
        return t('files.1');
      }
    }
  };

  return (
    <div
      className={
        data?.chatId === selectedUser?.chatId || data?.chatId === selectedChatTopic?.chatId
          ? classNames(styles.groupElement, styles.groupElement_active, {
              [styles.groupElement_smallActive]: selectedUser?.hasTopic && !isTopic,
            })
          : classNames(styles.groupElement, {
              [styles.groupElement_small]: selectedUser?.hasTopic && !isTopic,
            })
      }
      onClick={handleSelectGroup}
    >
      <div
        className={classNames(styles.groupElement__header, {
          [styles.groupElement__header_small]: selectedUser?.hasTopic && !isTopic,
        })}
      >
        <div
          className={classNames(styles.groupElement__box, {
            [styles.groupElement__box_small]: selectedUser?.hasTopic,
          })}
        >
          {!isTopic && <Avatar src={avatar} size={42} />}
          {(!selectedUser?.hasTopic || isTopic) && <p className={styles.groupElement__name}>{data?.chatName}</p>}
        </div>
        {(!selectedUser?.hasTopic || isTopic) && <p className={styles.groupElement__date}>{renderDate()}</p>}
      </div>
      {(!selectedUser?.hasTopic || isTopic) && (
        <div className={styles.groupElement__content}>
          <div className={styles.groupElement__message}>
            {data?.lastMessage && (
              <p
                className={
                  data?.chatId === selectedUser?.chatId || data?.chatId === selectedChatTopic?.chatId
                    ? classNames(styles.groupElement__userName, styles.groupElement__userName_active)
                    : styles.groupElement__userName
                }
              >
                {data?.lastMessage?.userId === currentUser.id ? t('you') : data?.lastMessage?.user_first_name}
              </p>
            )}
            {data?.lastMessage && <>{data?.lastMessage?.content ? data.lastMessage.content : renderFileText()}</>}
          </div>
          {!!data?.notReadMessages && <div className={styles.groupElement__noRead}>{data.notReadMessages}</div>}
        </div>
      )}
    </div>
  );
};

ChatElement.propTypes = {
  data: PropTypes.shape({
    chatId: PropTypes.number,
    chatName: PropTypes.string,
    chatPhotoUuid: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
    authorId: PropTypes.number,
    lastMessage: PropTypes.shape({
      content: PropTypes.string,
      createdAt: PropTypes.string,
      forwardedMessageId: PropTypes.oneOfType([PropTypes.number, PropTypes.object]),
      userId: PropTypes.number,
      user_first_name: PropTypes.string,
      user_last_name: PropTypes.string,
      user_local_photo_uuid: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    }),
    notReadMessages: PropTypes.number,
  }),
};

export default memo(ChatElement);
